// react
import { Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { AppBar, Box, useMediaQuery, useTheme } from '@mui/material';

// reuse bridge header/footer for now
import Footer from 'layout/CloudLayout/Footer';
import Header from 'layout/CloudLayout/Header';
import AspTags from 'asp/cloud';
import { Helmet } from 'react-helmet-async';
import { CLOUD_BASE_PATH } from 'utils/constants';

import banner from 'assets/images/Cloud/lp/sp-foot-banner.png';

const SpBanner = () => {
  const location = useLocation();
  const hide = location.pathname.startsWith(`${CLOUD_BASE_PATH}/ec`);
  return hide ? null : (
    <Link to={`${CLOUD_BASE_PATH}/ec`}>
      <Box
        component="img"
        src={banner}
        alt="スマホ用バナー"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          bgcolor: 'primary.main',
          color: '#fff',
          zIndex: 1300,
          display: { xs: 'block', md: 'none' }
        }}
      />
    </Link>
  );
};

const CloudLayout = () => {
  const theme = useTheme();
  const isPc = useMediaQuery(theme.breakpoints.up('sm'));
  const location = useLocation();
  const { pathname } = location;
  const hideChatPlus = pathname.startsWith(`${CLOUD_BASE_PATH}/mypage`) || pathname.startsWith(`${CLOUD_BASE_PATH}/ec`);
  const hideChrome =
    pathname === CLOUD_BASE_PATH ||
    pathname === `${CLOUD_BASE_PATH}/` ||
    pathname === `${CLOUD_BASE_PATH}/features` ||
    pathname === `${CLOUD_BASE_PATH}/plan` ||
    pathname === `${CLOUD_BASE_PATH}/global` ||
    pathname === `${CLOUD_BASE_PATH}/flow` ||
    pathname === `${CLOUD_BASE_PATH}/faq`;

  const headerRef = useRef<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const scriptId = 'chatplus-script';

    if (document.getElementById(scriptId)) return;

    (document as any).__cp_d = 'https://app.chatplus.jp';
    (document as any).__cp_c = '43a476bf_6';

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://app.chatplus.jp/cp.js';
    script.async = true;

    document.body.appendChild(script);

    return () => {
      // 常駐させたいなら削除しない
      // document.getElementById(scriptId)?.remove();
    };
  }, []);

  useEffect(() => {
    const selectors = [
      'iframe[src*="chatplus.jp"]',
      'iframe[src*="chatplus"]',
      '[id*="chatplus"]',
      '[class*="chatplus"]',
      '[id^="cp_"]',
      '[class^="cp_"]'
    ];

    const toggleChatPlus = () => {
      selectors.forEach((selector) => {
        document.querySelectorAll<HTMLElement>(selector).forEach((element) => {
          element.style.setProperty('display', hideChatPlus ? 'none' : '', 'important');
          element.style.setProperty('visibility', hideChatPlus ? 'hidden' : '', 'important');
          element.style.setProperty('pointer-events', hideChatPlus ? 'none' : '', 'important');
        });
      });
    };

    toggleChatPlus();

    const observer = new MutationObserver(() => {
      toggleChatPlus();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [hideChatPlus]);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0 });
    if (!isPc) {
      setHeaderHeight(0);
      return;
    }

    const el = headerRef.current;
    if (!el) return;

    const update = () => {
      setHeaderHeight(el.getBoundingClientRect().height);
    };

    update();

    if (typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isPc, pathname]);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Helmet>
        <title>【公式】クラウドWiFi｜月額1,580円から使えるレンタルサービス</title>
        <meta
          name="description"
          content="クラウドWiFi｜ライフスタイルに合わせて選べる4プラン！契約期間の縛りなし｜データチャージOK｜海外利用OK｜解約金0円のポケット型WiFiレンタルサービス｜10GB/20GB/50GB/100GB｜"
        />
        <link rel="icon" type="image/png" sizes="32x32" href={`${process.env.REACT_APP_PUBLIC_URL}/cloud.logo.png`} />
        <meta property="og:description" content="月額1,580円(税抜)から選べる4プラン。縛りなしのWiFiルーターレンタルサービス" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${process.env.REACT_APP_PUBLIC_URL}${process.env.REACT_APP_CLOUD_BASE_PATH}/`} />
        <meta property="og:image" content={`${process.env.REACT_APP_PUBLIC_URL}/ogp.jpg`} />
        <script src={`${process.env.REACT_APP_CLOUD_REMISE_ENDPOINT}/js/RemiseToken-min.js`}></script>
      </Helmet>
      {!hideChrome && (
        <>
          <AppBar ref={headerRef} position={isPc ? 'fixed' : 'static'}>
            <Header />
          </AppBar>
          {isPc && headerHeight > 0 && <Box sx={{ height: `${headerHeight}px` }} />}
        </>
      )}
      <Outlet />
      {!hideChrome && <Footer />}
      <SpBanner />
      {process.env.NODE_ENV === 'production' && <AspTags />}
    </Suspense>
  );
};

export default CloudLayout;
